import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { FormProvider, useForm } from "react-hook-form";
import { PaziaFormButton, PaziaInput, PaziaLoader, PaziaSwitch } from "components/atoms";
import { useGoogleAnalyticsConfig, shopApi, GoogleAnalyticsConfig } from "api/shop";
import { useToaster } from "utils";
import { useState, useEffect } from "react";

const FORM_NAMES = {
  MEASUREMENT_ID: "measurementId",
  ENABLED: "enabled",
} as const;

const MEASUREMENT_ID_REGEX = /^G-[A-Z0-9]+$/;

const schema = yup
  .object({
    [FORM_NAMES.MEASUREMENT_ID]: yup
      .string()
      .default("")
      .test(
        "measurement-id-format",
        "Identyfikator musi mieć format G-XXXXXXXXXX",
        (value) => !value || MEASUREMENT_ID_REGEX.test(value),
      ),
    [FORM_NAMES.ENABLED]: yup.boolean().default(false),
  })
  .required();

interface FormValues {
  measurementId: string;
  enabled: boolean;
}

export const ShopGoogleAnalyticsForm = () => {
  const { googleAnalyticsConfig, isLoading, invalidate } = useGoogleAnalyticsConfig();
  const { showSuccess, showError } = useToaster();
  const [isSaving, setIsSaving] = useState(false);

  const methods = useForm<FormValues>({
    resolver: yupResolver(schema) as any,
    defaultValues: {
      [FORM_NAMES.MEASUREMENT_ID]: googleAnalyticsConfig?.measurementId ?? "",
      [FORM_NAMES.ENABLED]: googleAnalyticsConfig?.enabled ?? false,
    },
  });
  const { handleSubmit, reset } = methods;

  useEffect(() => {
    if (googleAnalyticsConfig) {
      reset({
        [FORM_NAMES.MEASUREMENT_ID]: googleAnalyticsConfig.measurementId ?? "",
        [FORM_NAMES.ENABLED]: googleAnalyticsConfig.enabled ?? false,
      });
    }
  }, [googleAnalyticsConfig, reset]);

  const onSubmit = async (data: FormValues) => {
    setIsSaving(true);
    try {
      const formattedData: GoogleAnalyticsConfig = {
        measurementId: data.measurementId || undefined,
        enabled: data.enabled,
      };

      await shopApi.updateGoogleAnalyticsConfig(formattedData);
      showSuccess("Zapisano ustawienia Google Analytics");
      invalidate();
    } catch (error) {
      showError("Nie udało się zapisać ustawień Google Analytics");
      console.error(error);
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) return <PaziaLoader position="center" />;

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmit)} className="grid max-w-3xl gap-6">
        <div className="rounded-lg border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-800">
          <h3 className="mb-4 text-lg font-medium text-slate-800 dark:text-slate-200">Google Analytics (GA4)</h3>
          <div className="grid gap-4">
            <PaziaInput
              name={FORM_NAMES.MEASUREMENT_ID}
              label="Identyfikator pomiarowy GA4"
              placeholder="G-XXXXXXXXXX"
            />
            <PaziaSwitch name={FORM_NAMES.ENABLED} label="Włącz śledzenie Google Analytics" />
          </div>
        </div>

        <div className="rounded-lg border border-blue-200 bg-blue-50 p-4 dark:border-blue-800 dark:bg-blue-950">
          <h4 className="mb-2 text-sm font-semibold text-blue-800 dark:text-blue-300">Co jest śledzone?</h4>
          <ul className="space-y-1 text-sm text-blue-700 dark:text-blue-400">
            <li>• <strong>Odsłony stron</strong> — każde przejście na nową podstronę sklepu</li>
            <li>• <strong>Dodanie do koszyka</strong> (<code>add_to_cart</code>) — kliknięcie „Dodaj do koszyka" przy edycji kursu</li>
            <li>• <strong>Usunięcie z koszyka</strong> (<code>remove_from_cart</code>) — usunięcie kursu z koszyka</li>
            <li>• <strong>Podgląd koszyka</strong> (<code>view_cart</code>) — otwarcie strony koszyka (/zapisy)</li>
            <li>• <strong>Rozpoczęcie zakupu</strong> (<code>begin_checkout</code>) — kliknięcie przycisku zapisu w formularzu koszyka</li>
          </ul>
          <p className="mt-3 text-xs text-blue-600 dark:text-blue-500">
            Zdarzenia są wysyłane tylko gdy użytkownik zaakceptował ciasteczka analityczne (consent mode).
          </p>
        </div>

        <div className="flex justify-start">
          <PaziaFormButton disabled={isSaving} />
        </div>
      </form>
    </FormProvider>
  );
};
